import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Phone, ArrowLeft, Building2, Loader2, Tag, HandHelping, Megaphone, Flag, Users, Package } from "lucide-react";
import { Layout } from "@/components/Layout";
import { NetworkActions } from "@/components/NetworkActions";
import { VerifiedBadge, getTier } from "@/components/VerifiedBadge";
import { Button } from "@/components/ui/button";
import { api, mediaUrl, formatApiErrorDetail } from "@/lib/api";
import { timeAgo } from "@/lib/razorpay";
import { useAuth } from "@/context/AuthContext";

const FALLBACK = "https://images.unsplash.com/photo-1621831337128-35676ca30868?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBidWlsZGluZyUyMGZhY2FkZXxlbnwwfHx8fDE3ODgwOTU3MDJ8MA&ixlib=rb-4.1.0&q=85";
import { MediaGallery } from "@/components/MediaGallery";
const TABS = ["About", "Products & Services", "Gallery", "Offers", "Requirements", "Updates", "Info"];

export default function PublicProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [biz, setBiz] = useState(null);
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState("About");
  const [activity, setActivity] = useState({ Offers: [], Requirements: [], Updates: [] });

  const loadCard = useCallback(async () => {
    try { const { data } = await api.get("/discover", { params: { limit: 50 } }); setCard(data.items.find((x) => x.slug === id || x.id === id) || null); } catch (e) { console.error("Failed to load business card", e); }
  }, [id]);

  useEffect(() => {
    let active = true;
    const loadActivity = async (bid, type, key) => { try { const { data } = await api.get(`/businesses/${bid}/activity`, { params: { type, limit: 20 } }); if (active) setActivity((a) => ({ ...a, [key]: data.items })); } catch (e) { console.error(`Failed to load ${key}`, e); } };
    api.get(`/businesses/${id}`).then(({ data }) => {
      if (!active) return;
      setBiz(data);
      loadActivity(data.id, "Offer", "Offers");
      loadActivity(data.id, "Requirement", "Requirements");
      loadActivity(data.id, "Update", "Updates");
    }).catch(() => { if (active) setNotFound(true); }).finally(() => { if (active) setLoading(false); });
    loadCard();
    return () => { active = false; };
  }, [id, loadCard]);

  const report = async () => {
    if (!user) return toast.error("Log in to report");
    const reason = window.prompt("Why are you reporting this business?");
    if (!reason || !reason.trim()) return;
    try { await api.post("/reports", { target_type: "business", target_id: biz.id, reason: reason.trim() }); toast.success("Reported to moderators"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading) return <Layout><div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-brand" size={32} /></div></Layout>;
  if (notFound || !biz) return <Layout><div className="min-h-[50vh] flex flex-col items-center justify-center"><p className="font-display font-bold text-xl text-slate-800">Business not found</p><Link to="/discover" className="mt-4 text-brand font-semibold">← Back to Discover</Link></div></Layout>;

  const logo = mediaUrl(biz.logo_url);
  const cover = mediaUrl(biz.cover_url) || FALLBACK;
  const isUnclaimed = biz.claim_status === "unclaimed";

  const claim = async () => {
    if (!user) { toast.message("Create an account to claim this business"); navigate("/register"); return; }
    try {
      await api.post(`/businesses/${biz.id}/claim`, {});
      toast.success("Business claimed! It's now yours to manage.");
      navigate("/dashboard");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const PostList = ({ list, icon: Icon, empty }) => (
    list.length === 0 ? <p className="text-slate-400 py-8 text-center">{empty}</p> : (
      <div className="space-y-4">{list.map((p) => (
        <div key={p.id} data-testid={`activity-${p.id}`} className="border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-2 text-xs text-slate-400 mb-2"><Icon size={14} className="text-brand" /> {p.type} · {timeAgo(p.created_at)} · {p.comment_count} comments</div>
          <p className="text-slate-700 whitespace-pre-wrap">{p.content}</p>
        </div>))}</div>
    )
  );

  return (
    <Layout>
      <div className="mx-auto max-w-4xl px-5 sm:px-8 py-8">
        <Link to="/discover" data-testid="back-to-discover" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-brand mb-5"><ArrowLeft size={16} /> Discover</Link>

        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="h-40 sm:h-52 w-full overflow-hidden bg-slate-100 relative">
            <img src={cover} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-90" />
            <div className="absolute inset-0 bg-brand/10" />
          </div>
          <div className="px-6 sm:px-10 pb-6">
            <div className="-mt-14 mb-4 relative z-10 h-24 w-24 rounded-2xl border-4 border-white bg-white shadow-md overflow-hidden flex items-center justify-center">
              {logo ? <img src={logo} alt={biz.business_name} className="h-full w-full object-cover" /> : <Building2 className="text-slate-300" size={38} />}
            </div>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 data-testid="profile-business-name" className="font-display font-black tracking-tight text-2xl sm:text-3xl text-slate-900">{biz.business_name}</h1>
                  <span data-testid="profile-verified-badge"><VerifiedBadge tier={getTier(biz)} size="lg" /></span>
                </div>
                {biz.trade_name && <p className="text-slate-400 text-sm mt-0.5">{biz.trade_name}</p>}
                <div className="mt-1.5 flex items-center gap-3 text-sm text-slate-500 flex-wrap">
                  <Link
                    to={`/categories/${(biz.category || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}
                    data-testid="profile-category-link"
                    className="font-bold uppercase tracking-wide text-brand hover:underline"
                  >
                    {biz.category}
                  </Link>
                  {(biz.city || biz.state) && <span className="inline-flex items-center gap-1"><MapPin size={14} /> {[biz.city, biz.state].filter(Boolean).join(", ")}</span>}
                  {card && <span className="inline-flex items-center gap-1"><Users size={14} /> {card.follower_count} followers</span>}
                </div>
              </div>
            </div>

            {isUnclaimed && (
              <div data-testid="claim-business-banner" className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl border border-blue-200 bg-blue-50 p-4">
                <div className="flex-1">
                  <p className="font-bold text-slate-900 text-sm">Is this your business?</p>
                  <p className="text-sm text-slate-600">This verified profile is unclaimed. Claim it to manage your details, post updates and connect with the network.</p>
                </div>
                <Button data-testid="claim-business-btn" onClick={claim} className="bg-brand hover:bg-brand-hover font-semibold shrink-0">Claim This Business</Button>
              </div>
            )}

            {card && (
              <div className="mt-5 max-w-md">
                <NetworkActions business={card} onChange={loadCard} size="lg" />
              </div>
            )}

            {biz.intents?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">{biz.intents.map((i) => <span key={i} className="rounded-full bg-brand-light px-3 py-1 text-xs font-semibold text-brand">{i}</span>)}</div>
            )}
          </div>

          {/* Tabs */}
          <div className="border-t border-slate-200 px-4 sm:px-10 flex gap-1 overflow-x-auto">
            {TABS.map((t) => (
              <button key={t} data-testid={`profile-tab-${t}`} onClick={() => setTab(t)} className={`shrink-0 px-3 py-3 text-sm font-semibold border-b-2 transition-colors ${tab === t ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{t}</button>
            ))}
          </div>

          <div className="px-6 sm:px-10 py-6" data-testid="profile-tab-content">
            {tab === "About" && <div><p data-testid="business-description" className="text-slate-700 leading-relaxed whitespace-pre-wrap break-words">{biz.description || "No description provided yet."}</p>
              {biz.offerings?.length > 0 && <div className="mt-4 flex flex-wrap gap-2">{biz.offerings.map((o) => <span key={o} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{o}</span>)}</div>}</div>}
            {tab === "Products & Services" && (biz.products?.length > 0 ? <div className="flex flex-wrap gap-2">{biz.products.map((p, i) => <span key={`${p}-${i}`} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700"><Package size={14} className="text-brand" /> {p}</span>)}</div> : <p className="text-slate-400 py-6 text-center">No products or services listed yet.</p>)}
            {tab === "Gallery" && <MediaGallery businessId={biz.id} />}
            {tab === "Offers" && <PostList list={activity.Offers} icon={Tag} empty="No active offers." />}
            {tab === "Requirements" && <PostList list={activity.Requirements} icon={HandHelping} empty="No active requirements." />}
            {tab === "Updates" && <PostList list={activity.Updates} icon={Megaphone} empty="No updates yet." />}
            {tab === "Info" && <div className="grid sm:grid-cols-2 gap-3">
              <Info label="Category" value={biz.category} /><Info label="Business Type" value={biz.business_type || "—"} />
              <Info label="Location" value={[biz.city, biz.state].filter(Boolean).join(", ") || "—"} />
              <Info label="Verification" value={biz.verification_tier === "gst" ? "GST Verified" : biz.verification_tier === "msme" ? "MSME Verified" : "Unverified"} />
              <Info label="GSTIN" value={biz.gst_number || "—"} />
              {card?.connection?.status === "accepted" && biz.phone && <Info label="Phone (connections only)" value={biz.phone} />}
            </div>}
          </div>
        </div>

        <button data-testid="report-business" onClick={report} className="mt-4 inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-red-500"><Flag size={13} /> Report this business</button>
      </div>
    </Layout>
  );
}

const Info = ({ label, value }) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
    <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
    <p className="text-sm font-semibold text-slate-800 mt-0.5">{value}</p>
  </div>
);
